import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../Components/SnackBar";
import {
  apiGetRequest,
  apiPostRequest,
  apiDeleteRequest,
  apiPutRequest,
} from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { API_RECORDS_COUNT } from "../../../../Constants/config";
import ProductsTable from "./ProductsTable";
import ProductModal from "./ProductModal";
import DeleteProductDialog from "./DeleteProductDialog";
import PauseProductDialog from "./PauseProductDialog";
import FulfillmentReminderDialog from "./FulfillmentReminderDialog";
import {
  productsTableContainerStyle,
  productsHeaderStyle,
  productsHeaderLeftStyle,
  myProductsTitleStyle,
  addProductButtonStyle,
  searchFieldStyle,
  emptyStateBoxStyle,
  emptyStateTitleStyle,
  emptyStateDescriptionStyle,
} from "./styles";

const PAGE_SIZE = API_RECORDS_COUNT;

/**
 * Supplier catalog manager. Serves both verticals — stores (`products`) and
 * restaurant menus (`restaurants`) — because the BE exposes the same endpoints
 * for each. Only the endpoint segment differs; see endpoints.catalog().
 */
const ProductsContent = ({ vertical = "products" }) => {
  // Memoized so the fetch hooks below can depend on it without re-running on
  // every render — it only changes when the vertical does.
  const api = useMemo(() => endpoints.catalog(vertical), [vertical]);
  const { t } = useTranslation(["supplier-products", "categories"]);
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  // Open after a successful create when the BE flags
  // `fulfillment_missing` — i.e. the supplier just added a product but
  // has neither pickup nor any delivery district configured, so the
  // product is invisible to members. The dialog nudges them straight to
  // /business-settings.
  const [showFulfillmentReminder, setShowFulfillmentReminder] = useState(false);

  // Products data
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modals
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [duplicatingProduct, setDuplicatingProduct] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  // Pause dialog
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pausingProduct, setPausingProduct] = useState(null);
  const [pauseLoader, setPauseLoader] = useState(false);

  // Categories for the modal
  const [categories, setCategories] = useState([]);

  const loadMoreSentinelRef = useRef(null);

  // Cap the products scroll region so its bottom lands at the viewport
  // bottom: the list then scrolls internally and the page/window never
  // grows a second "big" scrollbar. We measure at runtime because the
  // shared supplier layout isn't a bounded flex column, so a static
  // height can't know how much chrome sits above this table.
  const scrollRegionRef = useRef(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState(undefined);

  useLayoutEffect(() => {
    const el = scrollRegionRef.current;
    if (!el) return;
    const recompute = () => {
      // Document-relative top (stable regardless of any transient scroll).
      const top = el.getBoundingClientRect().top + window.scrollY;
      // Leave a small gap so the last row clears the fixed footer.
      const FOOTER_GAP = 32;
      setScrollMaxHeight(
        `calc(100dvh - ${Math.ceil(top)}px - ${FOOTER_GAP}px)`
      );
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, []);

  // Debounce search
  useEffect(() => {
    if (searchTerm.length === 0) {
      setDebouncedSearch("");
      return;
    }
    if (searchTerm.length < 2) return;

    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Fetch products
  const fetchProducts = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (pageNum === 1) setLoading(true);
        else setLoadingMore(true);

        const params = new URLSearchParams();
        params.append("page", pageNum);
        params.append("page_size", PAGE_SIZE);
        if (debouncedSearch) params.append("search", debouncedSearch);
        const response = await apiGetRequest(
          `${api.supplierItems}?${params.toString()}`
        );

        if (response.status === 200) {
          const newProducts = response.data.products || [];
          if (append) {
            setProducts((prev) => [...prev, ...newProducts]);
          } else {
            setProducts(newProducts);
          }
          setHasMore(newProducts.length === PAGE_SIZE);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, api]
  );

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiGetRequest(api.getCategories);
        if (response.status === 200) {
          setCategories(response.data.categories || []);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [api]);

  // Re-fetch when search changes
  useEffect(() => {
    setPage(1);
    fetchProducts(1, false);
  }, [debouncedSearch, fetchProducts]);

  // Infinite scroll via IntersectionObserver — fires when the sentinel
  // becomes visible regardless of which container is scrolling.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchProducts(nextPage, true);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [page, hasMore, loadingMore, loading, fetchProducts]);

  // Actions
  const handleAddProduct = () => {
    setEditingProduct(null);
    setDuplicatingProduct(null);
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setDuplicatingProduct(null);
    setShowProductModal(true);
  };

  const handleDuplicateProduct = (product) => {
    setDuplicatingProduct(product);
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const handleDeleteClick = (product) => {
    setDeletingProduct(product);
    setShowDeleteDialog(true);
  };

  const handlePauseToggle = (product) => {
    setPausingProduct(product);
    setShowPauseDialog(true);
  };

  const confirmPauseToggle = async () => {
    if (!pausingProduct) return;
    const newStatus = pausingProduct.is_active ? "paused" : "active";
    try {
      setPauseLoader(true);
      const response = await apiPutRequest(
        api.updateItemStatus(pausingProduct.id),
        { status: newStatus }
      );
      if (response.status === 200) {
        setProducts((prev) =>
          prev.map((p) =>
            p.id === pausingProduct.id ? { ...p, is_active: !p.is_active } : p
          )
        );
        snackbar(
          t(newStatus === "paused" ? "pause_success" : "unpause_success"),
          "success"
        );
        setShowPauseDialog(false);
        setPausingProduct(null);
      }
    } catch (error) {
      console.error("Error toggling product status:", error);
    } finally {
      setPauseLoader(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingProduct) return;
    try {
      setDeleteLoader(true);
      const response = await apiDeleteRequest(
        api.deleteItem(deletingProduct.id)
      );
      if (response.status === 200) {
        snackbar(t("delete_dialog.confirm"), "success");
        setProducts((prev) => prev.filter((p) => p.id !== deletingProduct.id));
        setShowDeleteDialog(false);
        setDeletingProduct(null);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      // BE returns 409 + message "PRODUCT_HAS_ORDERS" when the product
      // is referenced by past order line items. We can't hard-delete
      // without destroying order history, so the right path is for the
      // supplier to pause the product instead. Show a clear toast that
      // explains, and close the delete dialog so they're not stuck.
      const beCode = error?.response?.data?.message;
      if (beCode === "PRODUCT_HAS_ORDERS") {
        snackbar(t("delete_dialog.has_orders_error"), "error");
        setShowDeleteDialog(false);
        setDeletingProduct(null);
      } else {
        snackbar(t("modal.errors.save_failed"), "error");
      }
    } finally {
      setDeleteLoader(false);
    }
  };

  const handleProductSaved = (savedProduct, isEdit, fulfillmentMissing) => {
    // A product with existing orders is versioned on edit: the BE marks the
    // old row DELETED and returns a NEW successor row with a different id. Swap
    // by the id we were editing, not the returned id, so the old row is
    // replaced (and not duplicated) in the list.
    const editedId = editingProduct?.id;

    setShowProductModal(false);
    setEditingProduct(null);
    setDuplicatingProduct(null);

    // Only fire on create (BE doesn't return the flag on edit anyway,
    // but be defensive). Shows the reminder dialog right after the
    // product modal closes, layered on top of the products list.
    if (!isEdit && fulfillmentMissing === true) {
      setShowFulfillmentReminder(true);
    }

    if (savedProduct) {
      if (isEdit) {
        // Replace the edited row (matched by the id we opened the modal with,
        // since versioning may have changed it) with the returned product.
        setProducts((prev) =>
          prev.map((p) => (p.id === editedId ? savedProduct : p))
        );
      } else {
        // Prepend the new product to the list
        setProducts((prev) => [savedProduct, ...prev]);
      }

      if (typeof savedProduct.tags === "string" && savedProduct.tags) {
        const savedTags = savedProduct.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        if (savedTags.length) {
          setTagSuggestions((prev) =>
            Array.from(new Set([...prev, ...savedTags]))
          );
        }
      }
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  const [tagSuggestions, setTagSuggestions] = useState([]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await apiGetRequest(api.getAllTags);
        if (response.status === 200) {
          setTagSuggestions(response.data.tags || []);
        }
      } catch (error) {
        console.error("Error fetching product tags:", error);
      }
    };
    fetchTags();
  }, [api]);

  const isEmpty = !loading && products.length === 0 && !debouncedSearch;
  const isSearchEmpty = !loading && products.length === 0 && debouncedSearch;

  return (
    <Box sx={productsTableContainerStyle}>
      {/* Header: My Products + Add button + Search */}
      <Box sx={productsHeaderStyle}>
        <Box sx={productsHeaderLeftStyle}>
          <Typography sx={myProductsTitleStyle}>{t("my_products")}</Typography>
          <Button
            variant="contained"
            onClick={handleAddProduct}
            sx={addProductButtonStyle}
          >
            {t("add_new_product")}
          </Button>
        </Box>

        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          placeholder={t("search_placeholder")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgba(62, 63, 76, 0.4)" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={searchFieldStyle}
        />
      </Box>

      {/* Content — the single scroll region for this tab. Capped to the
          viewport bottom (scrollMaxHeight) so the list scrolls here instead
          of growing the page and adding a second window scrollbar. */}
      <Box
        ref={scrollRegionRef}
        className="custom-scrollbar"
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: scrollMaxHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress size={60} sx={{ color: "#248B70" }} />
          </Box>
        ) : isEmpty ? (
          /* Empty state */
          <Box sx={emptyStateBoxStyle}>
            <img
              src="/icons/add-files-icons.svg"
              alt="no products"
              width="48"
              height="48"
              style={{ opacity: 0.6, marginBottom: "16px" }}
            />
            <Typography sx={emptyStateTitleStyle}>
              {t("no_products_yet")}
            </Typography>
            <Typography sx={emptyStateDescriptionStyle}>
              {t("no_products_yet_description")}
            </Typography>
          </Box>
        ) : isSearchEmpty ? (
          /* No search results */
          <Box sx={emptyStateBoxStyle}>
            <Typography sx={emptyStateTitleStyle}>
              {t("no_search_results")}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClearSearch}
              sx={{
                ...addProductButtonStyle,
                mt: "16px",
                borderRadius: "900px",
              }}
            >
              {t("clear_search")}
            </Button>
          </Box>
        ) : (
          <>
            <ProductsTable
              vertical={vertical}
              products={products}
              onEdit={handleEditProduct}
              onDuplicate={handleDuplicateProduct}
              onDelete={handleDeleteClick}
              onPauseToggle={handlePauseToggle}
            />
            {hasMore && <Box ref={loadMoreSentinelRef} sx={{ height: "1px" }} />}
            {loadingMore && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: "16px",
                }}
              >
                <CircularProgress size={32} sx={{ color: "#248B70" }} />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Product Modal (Add / Edit / Duplicate) */}
      {showProductModal && (
        <ProductModal
          vertical={vertical}
          open={showProductModal}
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
            setDuplicatingProduct(null);
          }}
          product={editingProduct || duplicatingProduct}
          isEdit={!!editingProduct}
          isDuplicate={!!duplicatingProduct}
          categories={categories}
          tagSuggestions={tagSuggestions}
          onProductSaved={handleProductSaved}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteDialog && deletingProduct && (
        <DeleteProductDialog
          open={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setDeletingProduct(null);
          }}
          onConfirm={confirmDelete}
          loading={deleteLoader}
        />
      )}

      {/* Pause/Unpause confirmation */}
      {showPauseDialog && pausingProduct && (
        <PauseProductDialog
          open={showPauseDialog}
          onClose={() => {
            setShowPauseDialog(false);
            setPausingProduct(null);
          }}
          onConfirm={confirmPauseToggle}
          loading={pauseLoader}
          isActive={pausingProduct.is_active}
        />
      )}

      {/* Fulfillment reminder — opens after a create when the BE flags
          that the supplier has no pickup AND no delivery districts set
          up. The product was created but isn't visible to members until
          they fix that. Single primary action: navigate to settings.
          X / backdrop / Escape all dismiss (it's a soft nudge). */}
      <FulfillmentReminderDialog
        open={showFulfillmentReminder}
        onClose={() => setShowFulfillmentReminder(false)}
        onGoToSettings={() => {
          setShowFulfillmentReminder(false);
          navigate("/business-settings");
        }}
      />
    </Box>
  );
};

export default ProductsContent;
